import { ChangeEvent, FC, FormEvent, useState } from 'react';

import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddAPhoto, Inventory, PhotoCamera } from '@mui/icons-material';
import { AxiosResponse } from 'axios';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import axios from '../../../sharedModules/utils/axios.utils';

import { getWarehouseSelectedLocation } from '../../navigation/components/WarehouseLocationToggle';
import WarehouseSelect from '../../warehouseOptions/components/WarehouseSelect';
import PurchaseOrderBanners from '../components/PurchaseOrderBanners';
import ReceivingItemsSection from '../components/ReceivingItemsSection';
import {
  MAX_SCAN_PAGES,
  PO_NUMBER_FORMAT,
  PhotoScanResult,
  PoCandidate,
  PurchaseOrderLookup,
  ReceivingDetail,
} from '../model';

const LOCATION_FIELDS = ['rack', 'shelf', 'bin'] as const;

type LocationField = (typeof LOCATION_FIELDS)[number];

const LOCATION_LABELS: Record<LocationField, string> = {
  rack: 'Rack',
  shelf: 'Shelf',
  bin: 'Bin',
};

const EMPTY_LOCATION: Record<LocationField, string> = { rack: '', shelf: '', bin: '' };
const NO_PREFILL: Record<LocationField, boolean> = { rack: false, shelf: false, bin: false };

// Count stays digits-only so barcode guns do not inject e/+/−/. from type=number quirks.
// Rack/Shelf/Bin accept alphanumeric ServMan location codes as returned by lookup.
const digitsOnly = (value: string): string => value.replace(/\D/g, '');

// A PO is digits, optionally behind a single leading R. Uppercased on the way in because the
// backend keys receivings on the PO and treats "r37735" and "R37735" as the same one; stripping
// an R anywhere but the front keeps a mistyped "3R7735" from looking like a valid PO.
const sanitizePoNumber = (value: string): string =>
  value
    .toUpperCase()
    .replace(/[^0-9R]/g, '')
    .replace(/(?!^)R/g, '');

const PREFILLED_STYLE = { '& .MuiOutlinedInput-root': { backgroundColor: '#FFF9C4' } };

const ReceivingPage: FC = () => {
  const [poNumber, setPoNumber] = useState<string>('');
  const [count, setCount] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [locationFields, setLocationFields] = useState<Record<LocationField, string>>(EMPTY_LOCATION);
  // Tracks which values came from the PO rather than from the receiver, so the yellow highlight
  // never claims credit for something they typed themselves.
  const [prefilled, setPrefilled] = useState<Record<LocationField, boolean>>(NO_PREFILL);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderLookup>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [scanning, setScanning] = useState<boolean>(false);
  // Pages the receiver has photographed but not yet sent: they are held here so a multi-page
  // document goes to the model as one set, and so nothing is uploaded until they say scan.
  const [stagedDocuments, setStagedDocuments] = useState<File[]>([]);
  const [candidates, setCandidates] = useState<PoCandidate[]>([]);
  // Bumped after a scan so the items section reloads what the scan wrote.
  const [reloadToken, setReloadToken] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // A document on the server, or staged pages that submit will attach without scanning.
  const [hasDocumentPhoto, setHasDocumentPhoto] = useState<boolean>(false);
  // A submit carries no line items - they are persisted by the section's own save - so finalizing
  // over unsaved edits would file the scan's original values and discard the receiver's corrections.
  const [hasUnsavedItems, setHasUnsavedItems] = useState<boolean>(false);
  // A save or photo upload inside the items section. Folded into busy so a submit cannot stitch
  // the receipt while photos it should contain are still uploading.
  const [itemsBusy, setItemsBusy] = useState<boolean>(false);
  // Survives the post-submit reset: it is the receiver's only handle on the receipt they made.
  const [receipt, setReceipt] = useState<{ poNumber: string; url: string | null }>();

  const handleLocationChange = (field: LocationField, value: string) => {
    setLocationFields((previous) => ({ ...previous, [field]: value }));
    setPrefilled((previous) => ({ ...previous, [field]: false }));
  };

  const applyLookup = (result: PurchaseOrderLookup) => {
    setPurchaseOrder(result);
    setLocationFields({
      rack: result.rack || '',
      shelf: result.shelf || '',
      bin: result.bin || '',
    });
    setPrefilled({
      rack: !!result.rack,
      shelf: !!result.shelf,
      bin: !!result.bin,
    });
    setErrorMessage('');
  };

  // On failure: clear PO banners and prior PO location so receivers never shelf against a stale
  // suggestion. Preserve Count only — that is the receiver's own entry, not the PO's.
  const clearLookupSuggestion = () => {
    setPurchaseOrder(undefined);
    setLocationFields(EMPTY_LOCATION);
    setPrefilled(NO_PREFILL);
  };

  // Extracted from handleLookUp so a scan can run the same lookup without a form event.
  const runLookup = async (po: string) => {
    setLoading(true);
    // Clear any scan candidates so a manual lookup's fresh result is never shown alongside chips
    // from a document the receiver has already moved on from.
    setCandidates([]);

    try {
      // Match the backend LOOKUP_TIMEOUT (40s) with a small buffer for round-trip.
      const response = await axios.get<PurchaseOrderLookup>(`/warehouse/receiving/purchase_orders/${po}`, {
        params: { location: getWarehouseSelectedLocation() },
        timeout: 45000,
      });
      applyLookup(response.data);
    } catch (error: any) {
      clearLookupSuggestion();
      setErrorMessage(error?.response?.data?.error || 'Something went wrong looking up this PO.');
    } finally {
      setLoading(false);
    }
  };

  const acceptCandidate = (value: string) => {
    setPoNumber(value);
    // Chips and the stale "couldn't read" message both belong to the scan that's being replaced.
    setErrorMessage('');
    runLookup(value); // runLookup clears candidates itself, so no redundant setCandidates([]) here.
  };

  // The single place the cap is applied, because the document area's own picker adds pages too
  // and would otherwise walk straight past the Add button's disabled state into a 422.
  const stageDocuments = (files: File[]) => setStagedDocuments(files.slice(0, MAX_SCAN_PAGES));

  const handleAddDocuments = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    // Reset the input immediately so photographing the same document twice still fires onChange.
    event.target.value = '';
    if (files.length === 0) return;

    stageDocuments([...stagedDocuments, ...files]);
  };

  const handleScan = async () => {
    if (stagedDocuments.length === 0) return;

    setScanning(true);
    setCandidates([]);
    setErrorMessage('');

    const formData = new FormData();
    stagedDocuments.forEach((file) => formData.append('document_photos[]', file));
    formData.append('location', getWarehouseSelectedLocation());

    try {
      // 70s against the backend's 60s Hatz timeout, matching the buffer the PO lookup uses.
      // (axios 0.x types the post<T> generic as the *request* body, so the response type is
      // passed via R instead — same pattern as PhotosSection.tsx's chiller log photo upload.)
      const response = await axios.post<FormData, AxiosResponse<PhotoScanResult>>(
        '/warehouse/receiving/photo_sets',
        formData,
        {
          timeout: 70000,
        },
      );
      const scan = response.data;

      // The pages are now attached to the receiving server-side, so holding them locally would
      // show every one of them twice in the document area.
      setStagedDocuments([]);

      // Only a high-confidence read fills the field on its own. Anything less is offered as a
      // choice, because a wrong PO shelves material in the wrong place.
      if (scan.confidence === 'high' && scan.po_number) {
        setPoNumber(scan.po_number);
        runLookup(scan.po_number);
      } else {
        // Only offer a candidate as a tap-to-fill chip if it could be a TUDI PO - the model is
        // asked to list every reference number it sees across every page, including ones it
        // rejected (vendor numbers, invoice numbers), and a chip carrying one of those looks up
        // cleanly against the wrong PO with the UI having implicitly endorsed it. The full,
        // unfiltered list is still persisted server-side on the photo set for diagnosing a bad read.
        const poCandidates = (scan.candidates || []).filter((c) => PO_NUMBER_FORMAT.test(c.value));
        setCandidates(poCandidates);
        setErrorMessage(
          poCandidates.length > 0
            ? "Couldn't read the PO with confidence. Pick one below or type it in."
            : "Couldn't find a PO number on these documents. Type it in.",
        );
      }
      // The scan just wrote the document photos and the table; tell the section to reload them.
      setReloadToken((token) => token + 1);
    } catch (error: any) {
      // The pages are deliberately kept staged on failure: a gateway blip must not cost the
      // receiver the photos they already took.
      setErrorMessage(error?.response?.data?.error || 'Something went wrong reading those photos.');
    } finally {
      setScanning(false);
    }
  };

  // Receivers work through a pallet one PO at a time, so a finished submit hands back an empty
  // page rather than the previous receiving's numbers.
  const resetForNextReceiving = () => {
    setPoNumber('');
    setCount('');
    setWarehouseId('');
    setLocationFields(EMPTY_LOCATION);
    setPrefilled(NO_PREFILL);
    setPurchaseOrder(undefined);
    setCandidates([]);
    setHasDocumentPhoto(false);
    setHasUnsavedItems(false);
    setStagedDocuments([]);
  };

  // Photos added without Scan live in stagedDocuments until now. Attach them first so submit
  // sees a document on the receiving; Scan still goes through photo_sets (OCR) instead.
  const attachStagedDocuments = async () => {
    if (stagedDocuments.length === 0) return;

    const formData = new FormData();
    stagedDocuments.forEach((file) => formData.append('document_photos[]', file));
    formData.append('po_number', poNumber);
    formData.append('location', getWarehouseSelectedLocation());

    await axios.post<FormData, AxiosResponse<ReceivingDetail>>('/warehouse/receiving/document_photos', formData, {
      timeout: 60000,
    });
    setStagedDocuments([]);
    setHasDocumentPhoto(true);
    setReloadToken((token) => token + 1);
  };

  // A submit finalizes the receiving that is already on the server - the photos and the
  // equipment table were written as the receiver worked - so it sends only their put-away
  // entries and gets the stitched receipt back.
  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorMessage('');
    setReceipt(undefined);

    try {
      await attachStagedDocuments();

      const response = await axios.post<unknown, AxiosResponse<ReceivingDetail>>(
        '/warehouse/receiving/submission',
        {
          po_number: poNumber,
          location: getWarehouseSelectedLocation(),
          warehouse_wh_id: warehouseId,
          received_count: count,
          ...locationFields,
        },
        // The receipt is stitched inline from every photo on the receiving, so this runs longer
        // than an ordinary write.
        { timeout: 60000 },
      );

      setReceipt({ poNumber, url: response.data.receipt_pdf_url });
      resetForNextReceiving();
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || 'Something went wrong submitting this receiving.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLookUp = async (event: FormEvent) => {
    event.preventDefault();
    await runLookup(poNumber);
  };

  const anyPrefilled = LOCATION_FIELDS.some((field) => prefilled[field]);
  // A lookup and a scan both end by writing purchaseOrder/locationFields/prefilled, so letting
  // one start while the other is in flight lets whichever response lands last silently overwrite
  // the other's result. Gating both buttons on either flag keeps only one such write in flight.
  const busy = loading || scanning || submitting || itemsBusy;
  // The same rule the backend enforces; the disabled button is the courtesy, not the guard.
  // Staged pages count: they are attached on submit without a scan.
  const canSubmit =
    !busy && !!poNumber && (hasDocumentPhoto || stagedDocuments.length > 0) && !hasUnsavedItems && !!warehouseId && !!count;

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader titleIcon={<Inventory />} mainTitle="Receiving" />
      </Grid>
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <Paper style={{ padding: 16 }}>
          <form onSubmit={handleLookUp}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm>
                <TextField
                  fullWidth
                  autoFocus
                  label="PO Number"
                  value={poNumber}
                  onChange={(event) => setPoNumber(sanitizePoNumber(event.target.value))}
                  inputProps={{ autoCapitalize: 'characters' }}
                />
              </Grid>
              <Grid item>
                <Button type="submit" variant="contained" size="large" disabled={busy || !poNumber}>
                  {loading ? <CircularProgress size={24} /> : 'Look Up'}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="large"
                  component="label"
                  disabled={busy || stagedDocuments.length >= MAX_SCAN_PAGES}
                  startIcon={<AddAPhoto />}
                >
                  Add Document
                  {/* capture opens the camera directly instead of the photo library; iOS hands
                      back one shot per tap, so extra pages are added by tapping again. */}
                  <input
                    hidden
                    multiple
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleAddDocuments}
                  />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleScan}
                  disabled={busy || stagedDocuments.length === 0}
                  startIcon={scanning ? <CircularProgress size={20} /> : <PhotoCamera />}
                >
                  {scanning ? 'Reading...' : `Scan Documents (${stagedDocuments.length})`}
                </Button>
              </Grid>
            </Grid>
          </form>
          {candidates.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap style={{ marginTop: 12 }}>
              {candidates.map((candidate) => (
                <Chip
                  key={`${candidate.label_on_document}-${candidate.value}`}
                  label={`${candidate.value} (${candidate.label_on_document})`}
                  onClick={() => acceptCandidate(candidate.value)}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
          <Divider style={{ margin: '24px 0' }} />
          <ReceivingItemsSection
            poNumber={poNumber}
            location={getWarehouseSelectedLocation()}
            reloadToken={reloadToken}
            onError={setErrorMessage}
            onDocumentPhotoChange={setHasDocumentPhoto}
            onDirtyChange={setHasUnsavedItems}
            onBusyChange={setItemsBusy}
            stagedDocuments={stagedDocuments}
            onStagedDocumentsChange={stageDocuments}
          />
          {purchaseOrder && (
            <div style={{ marginBottom: 24 }}>
              <PurchaseOrderBanners purchaseOrder={purchaseOrder} />
            </div>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <WarehouseSelect
                value={warehouseId}
                onChange={setWarehouseId}
                location={getWarehouseSelectedLocation()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Count"
                value={count}
                onChange={(event) => setCount(digitsOnly(event.target.value))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Grid>
            {LOCATION_FIELDS.map((field) => (
              <Grid item xs={12} sm={6} md={3} key={field}>
                <TextField
                  fullWidth
                  label={LOCATION_LABELS[field]}
                  value={locationFields[field]}
                  onChange={(event) => handleLocationChange(field, event.target.value)}
                  inputProps={{ autoCapitalize: 'characters' }}
                  sx={prefilled[field] ? PREFILLED_STYLE : undefined}
                />
              </Grid>
            ))}
          </Grid>
          {anyPrefilled && (
            <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 8 }}>
              Highlighted fields were prefilled from the PO. Edit any of them to override.
            </Typography>
          )}
          {receipt && (
            <Alert severity={receipt.url ? 'success' : 'warning'} style={{ marginTop: 16 }}>
              {receipt.url ? (
                <>
                  PO {receipt.poNumber} submitted.{' '}
                  <Link href={receipt.url} target="_blank" rel="noopener">
                    Open the receipt PDF
                  </Link>
                </>
              ) : (
                `PO ${receipt.poNumber} was submitted, but the receipt PDF could not be generated. Look the PO up and submit again to retry.`
              )}
            </Alert>
          )}
          <Grid container justifyContent="flex-end" alignItems="center" spacing={1} style={{ marginTop: 16 }}>
            {hasUnsavedItems && (
              <Grid item>
                {/* The section's own "Unsaved changes" note is far up the page on a phone; without
                    a reason here the disabled button just looks broken. */}
                <Typography variant="caption" color="warning.main">
                  Save Items before submitting.
                </Typography>
              </Grid>
            )}
            <Grid item>
              <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
                {submitting ? <CircularProgress size={24} /> : 'Submit Receiving'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ReceivingPage;
